import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Tabs, Tab,
  Avatar, IconButton, Snackbar, Alert, Menu, MenuItem
} from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, usePublicClient, useWalletClient, useChainId, useSwitchChain } from 'wagmi';
import { auth } from '../../components/firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { erc20Abi as ERC20_ABI, parseUnits, formatUnits } from 'viem';

/* ====== config ====== */
const CONTRACTS = {
  USDT: { token: '0xUSDT_TOKEN', staking: '0xYOUR_STAKING', decimals: 6 },
  USDC: { token: '0xUSDC_TOKEN', staking: '0xYOUR_STAKING', decimals: 6 },
  DAI:  { token: '0xDAI_TOKEN',  staking: '0xYOUR_STAKING', decimals: 18 },
  USDe: { token: '0xUSDE_TOKEN', staking: '0xYOUR_STAKING', decimals: 18 },
};
const STABLES = ['USDT','USDC','DAI','USDe'];
const LOGO = {
  USDT: 'https://assets.coingecko.com/coins/images/325/standard/Tether.png?1696501400',
  USDC: 'https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694',
  DAI:  'https://assets.coingecko.com/coins/images/9956/standard/Badge_Dai.png?1696507713',
  USDe: 'https://coin-images.coingecko.com/coins/images/33613/standard/usde.png?1733810059',
  prUSD:'https://assets.coingecko.com/coins/images/6319/standard/usdc.png?1696506694',
};
const PRUSD = { symbol: 'prUSD', decimals: 6 };
const STAKING_ABI = [
  { type: 'function', name: 'stake',    stateMutability: 'nonpayable', inputs: [{ name:'token', type:'address' }, { name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'stake',    stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'unstake',  stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'redeem',   stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
  { type: 'function', name: 'withdraw', stateMutability: 'nonpayable', inputs: [{ name:'amount', type:'uint256' }], outputs: [] },
];

// === BLACK GLASS THEME ===
const R12 = '12px';
const GLASS = {
  surface: 'linear-gradient(180deg, rgba(17,22,30,.92) 0%, rgba(17,22,30,.82) 70%)',
  border: '1px solid rgba(255,255,255,.14)',
  text: '#eef1f6', subtext: '#b8c0cc',
  tile: 'rgba(21,27,38,.60)',
};

// helpers
const pow10n = (p) => { let x = 1n; for (let i=0;i<p;i++) x*=10n; return x; };
const convertDecimals = (bn, fromDec, toDec) => fromDec===toDec ? bn : (fromDec<toDec ? bn*pow10n(toDec-fromDec) : bn/pow10n(fromDec-toDec));
const safeParse = (v, dec) => {
  try { const s = String(v ?? '').trim(); if (!s) return 0n; const bn = parseUnits(s, dec); return bn>0n?bn:0n; }
  catch { return 0n; }
};

export default function SwapCard({ onNotify }) {
  const { open } = useAppKit();
  const { isConnected, address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const navigate = useNavigate();
  const location = useLocation();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const requireAuth = () => {
    const u = auth.currentUser;
    if (!u) {
   navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { state: { background: location } });
   return false;
 }
 if (!u.emailVerified) {
   navigate('/verify-email', { state: { email: u.email }, replace: true });
   return false;
 }
    return true;
  };

  const [mode, setMode] = useState('swap');      // 'swap' | 'redeem'
  const [dir, setDir]   = useState('in');        // 'in': Stable->prUSD | 'out': prUSD->Stable
  const [sym, setSym]   = useState('USDT');

  const conf = useMemo(() => CONTRACTS[sym], [sym]);
  const tokenAddr   = conf?.token;
  const stakingAddr = conf?.staking;
  const decimals    = conf?.decimals ?? 6;

  const [amountIn, setAmountIn]     = useState('');
  const [amountOut, setAmountOut]   = useState('');
  const [lastEdit, setLastEdit]     = useState('in');
  const [stableBal, setStableBal]   = useState(0n);
  const [prUSDBal]     = useState(0n);
  const [allowance, setAllowance]   = useState(0n);
  const [snack, setSnack]           = useState('');
  const [busy, setBusy]             = useState(false);

  const PRUSD_TOKEN_ADDRESS = null; // If ERC20 prUSD appears, please provide the address.

  // balances/allowance
  useEffect(() => {
    let stop = false;
    (async () => {
      if (!publicClient || !address || !tokenAddr) return;
      try {
        const [bal, alw] = await Promise.all([
          publicClient.readContract({ address: tokenAddr, abi: ERC20_ABI, functionName: 'balanceOf', args: [address] }),
          publicClient.readContract({ address: tokenAddr, abi: ERC20_ABI, functionName: 'allowance',  args: [address, stakingAddr] }),
        ]);
        if (!stop) { setStableBal(bal); setAllowance(alw); }
      } catch {}
    })();
    return () => { stop = true; };
  }, [publicClient, address, tokenAddr, stakingAddr]);

  // sync inputs
  useEffect(() => {
    if (lastEdit === 'in') {
      const bn = safeParse(amountIn, dir === 'in' ? decimals : PRUSD.decimals);
      const conv = dir === 'in'
        ? convertDecimals(bn, decimals, PRUSD.decimals)
        : convertDecimals(bn, PRUSD.decimals, decimals);
      const d = dir === 'in' ? PRUSD.decimals : decimals;
      setAmountOut(conv === 0n ? '' : (Number(conv) / Number(pow10n(d))).toString());
    } else {
      const bn = safeParse(amountOut, dir === 'in' ? PRUSD.decimals : decimals);
      const conv = dir === 'in'
        ? convertDecimals(bn, PRUSD.decimals, decimals)
        : convertDecimals(bn, decimals, PRUSD.decimals);
      const d = dir === 'in' ? decimals : PRUSD.decimals;
      setAmountIn(conv === 0n ? '' : (Number(conv) / Number(pow10n(d))).toString());
    }
  }, [amountIn, amountOut, dir, decimals, lastEdit]);

  const flipDir = () => {
    setDir(d => d === 'in' ? 'out' : 'in');
    setAmountIn(amountOut);
    setAmountOut(amountIn);
    setLastEdit('in');
  };
  const setMax = () => {
    if (dir === 'in') {
      setLastEdit('in'); setAmountIn(formatUnits(stableBal, decimals));
    } else {
      setLastEdit('in'); setAmountIn(PRUSD_TOKEN_ADDRESS ? formatUnits(prUSDBal, PRUSD.decimals) : '');
    }
  };

  const ensureAllowance = async (amt) => {
    if (allowance >= amt) return;
    try {
      const tx = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve',
        args: [stakingAddr, amt], account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: tx });
    } catch {
      const tx0 = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve',
        args: [stakingAddr, 0n], account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: tx0 });
      const tx1 = await walletClient.writeContract({
        address: tokenAddr, abi: ERC20_ABI, functionName: 'approve',
        args: [stakingAddr, amt], account: address,
      });
      await publicClient.waitForTransactionReceipt({ hash: tx1 });
    }
    const fresh = await publicClient.readContract({
      address: tokenAddr, abi: ERC20_ABI, functionName: 'allowance', args: [address, stakingAddr]
    });
    setAllowance(fresh);
  };

  const onSwap = async () => {
    if (!requireAuth()) return;
    if (!isConnected || !walletClient) {
      return open?.({ view: 'Connect' });
    }
    if (chainId !== 1) {
      try { await switchChain({ chainId: 1 }); } catch { 
        setSnack('Please switch to Ethereum Mainnet'); 
        return; 
      }
    }
    if (!stakingAddr) return setSnack('Config error');
    try {
      setBusy(true);
      if (dir === 'in') {
        const amt = safeParse(amountIn, decimals); if (amt <= 0n) throw new Error('Enter amount');
        await ensureAllowance(amt);
        try {
          const h = await walletClient.writeContract({
            address: stakingAddr, abi: STAKING_ABI, functionName: 'stake',
            args: [tokenAddr, amt], account: address,
          });
          await publicClient.waitForTransactionReceipt({ hash: h });
        } catch {
          const h2 = await walletClient.writeContract({
            address: stakingAddr, abi: STAKING_ABI, functionName: 'stake',
            args: [amt], account: address,
          });
          await publicClient.waitForTransactionReceipt({ hash: h2 });
        }
      } else {
        const amt = safeParse(amountIn, PRUSD.decimals); if (amt <= 0n) throw new Error('Enter amount');
        let sent = false;
        for (const fn of ['unstake','redeem','withdraw']) {
          try {
            const h = await walletClient.writeContract({
              address: stakingAddr, abi: STAKING_ABI, functionName: fn, args: [amt], account: address,
            });
            await publicClient.waitForTransactionReceipt({ hash: h });
            sent = true; break;
          } catch {}
        }
        if (!sent) throw new Error('No unstake/redeem method');
      }
      setSnack('Swap completed ✅'); onNotify?.('Swap completed ✅');
      setAmountIn(''); setAmountOut('');
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Swap failed';
      setSnack(msg); onNotify?.(msg);
    } finally { setBusy(false); }
  };

  const balanceFrom = dir === 'in'
    ? `${formatUnits(stableBal, decimals)} ${sym}`
    : (PRUSD_TOKEN_ADDRESS ? `${formatUnits(prUSDBal, PRUSD.decimals)} ${PRUSD.symbol}` : '—');
  const balanceTo = dir === 'in'
    ? (PRUSD_TOKEN_ADDRESS ? `${formatUnits(prUSDBal, PRUSD.decimals)} ${PRUSD.symbol}` : '—')
    : `${formatUnits(stableBal, decimals)} ${sym}`;

  return (
    <>
      <Card sx={{
        maxWidth: 560, mx: 'auto', borderRadius: R12,
        background: GLASS.surface, border: GLASS.border, color: GLASS.text,
        boxShadow: '0 12px 32px rgba(2,8,23,.45)', backdropFilter: 'blur(10px)',
        '& .MuiOutlinedInput-root': {
          background: 'rgba(255,255,255,.08)', borderRadius: R12,
          '& fieldset': { borderColor: 'rgba(255,255,255,.18)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,.26)' },
          '&.Mui-focused fieldset': { borderColor: '#14F195' },
        },
        '& .MuiInputBase-input': { color: GLASS.text },
        '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,.55)' },
      }}>
        <CardContent sx={{ p: { xs: 1.75, md: 2 } }}>
          <Tabs
            value={mode}
            onChange={(_,v)=> setMode(v)}
            sx={{
              minHeight: 36, height: 36, mb: 1.25,
              '& .MuiTab-root': { minHeight: 36, height: 36, textTransform: 'none', fontWeight: 900, color: GLASS.text },
              '& .MuiTabs-indicator': { backgroundColor: '#14F195' },
            }}
          >
            <Tab value="swap"   label="Swap" />
            <Tab value="redeem" label="Redeem CrM" />
          </Tabs>

          {mode === 'swap' && (
            <>
              {/* FROM */}
              <Box sx={{ p: 1.25, borderRadius: R12, border: GLASS.border, background: GLASS.tile }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
                  <TokenPicker
                    value={dir === 'in' ? sym : 'prUSD'}
                    onChange={(v) => { if (dir === 'in') setSym(v); }}
                    disabled={dir !== 'in'}
                    label="From"
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: GLASS.subtext }}>
                    Balance: {balanceFrom}
                  </Typography>
                  <Button size="small" variant="outlined" onClick={setMax} sx={{ ml: 1, borderRadius: R12, textTransform: 'none' }}>
                    Max
                  </Button>
                </Box>
                <TextField
                  fullWidth placeholder="0" value={amountIn}
                  onChange={(e)=>{ setLastEdit('in'); setAmountIn(e.target.value); }}
                  inputMode="decimal"
                />
              </Box>

              {/* center button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', my: .5 }}>
                <IconButton
                  onClick={flipDir}
                  sx={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: GLASS.border, background: 'rgba(255,255,255,.05)',
                    '&:hover': { background: 'rgba(255,255,255,.10)' }
                  }}
                >
                  <SwapVertIcon sx={{ color: '#fff' }} />
                </IconButton>
              </Box>

              {/* TO */}
              <Box sx={{ p: 1.25, borderRadius: R12, border: GLASS.border, background: GLASS.tile }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .5 }}>
                  <TokenPicker
                    value={dir === 'in' ? 'prUSD' : sym}
                    onChange={(v) => { if (dir === 'out') setSym(v); }}
                    disabled={dir !== 'out'}
                    label="To"
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: GLASS.subtext }}>
                    Balance: {balanceTo}
                  </Typography>
                </Box>
                <TextField
                  fullWidth placeholder="0" value={amountOut}
                  onChange={(e)=>{ setLastEdit('out'); setAmountOut(e.target.value); }}
                  inputMode="decimal"
                />
              </Box>

              {/* main button */}
              <Button
                fullWidth variant="contained"
                onClick={
      isConnected
        ? onSwap
        : () => { if (requireAuth()) open?.({ view: 'Connect' }); }
    }
                disabled={isConnected ? (busy || !amountIn || Number(amountIn) <= 0) : false}
                sx={{
                  mt: 1.25, height: 44, borderRadius: 999, textTransform: 'none', fontWeight: 900,
                  background: 'linear-gradient(90deg, #2d74d7 0%, #14F195 100%)',
                }}
              >
                {isConnected ? (busy ? 'Processing…' : 'Swap') : 'Connect Wallet'}
              </Button>
            </>
          )}

          {mode === 'redeem' && (
            <>
              <Box sx={{ p: 1.25, borderRadius: R12, border: GLASS.border, background: GLASS.tile }}>
                <Typography sx={{ fontWeight: 800, mb: .5 }}>Redeem CrM → prUSD</Typography>
                <Typography variant="body2" sx={{ color: GLASS.subtext }}>
                  Coming soon. Rewards (CrM) redemption flow will appear here.
                </Typography>
              </Box>
              <Button fullWidth variant="contained" disabled sx={{
                mt: 1.25, height: 44, borderRadius: 999, textTransform: 'none', fontWeight: 900,
                background: 'linear-gradient(90deg, #2d74d7 0%, #14F195 100%)', opacity: .6
              }}>
                Redeem (coming soon)
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Snackbar  */}
      <Snackbar open={!!snack} autoHideDuration={3200} onClose={() => setSnack('')}>
        <Alert
          onClose={() => setSnack('')}
          severity="info"
          variant="filled"
          sx={{
            borderRadius: R12,
            background: 'rgba(17,22,30,.92)',
            color: GLASS.text,
            border: GLASS.border,
            backdropFilter: 'blur(6px)'
          }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </>
  );
}

function TokenPicker({ value, onChange, disabled, label }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isValidAnchor =
    !!(anchorEl && document.body.contains(anchorEl) && anchorEl.offsetParent !== null);

  useEffect(() => {
    if (anchorEl && !isValidAnchor) setAnchorEl(null);
  }, [anchorEl, isValidAnchor]);

  const isPr = value === 'prUSD';

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
        startIcon={<Avatar src={isPr ? LOGO.prUSD : LOGO[value]} sx={{ width: 22, height: 22 }} />}
        endIcon={<ExpandMoreIcon />}
        disabled={disabled}
        sx={{
          textTransform: 'none', borderRadius: R12, border: GLASS.border, color: GLASS.text, minWidth: 132,
          '& .MuiSvgIcon-root': { color: GLASS.text },
          '&.Mui-disabled': { color: GLASS.text, borderColor: GLASS.border, opacity: 1 },
          '&.Mui-disabled .MuiSvgIcon-root': { color: GLASS.text }
        }}
      >
        {label ? `${label}: ${value}` : value}
      </Button>

      <Menu
        anchorEl={isValidAnchor ? anchorEl : null}
        open={isValidAnchor}
        onClose={() => setAnchorEl(null)}
        keepMounted
      >
        {STABLES.map((s) => (
          <MenuItem key={s} onClick={() => { onChange?.(s); setAnchorEl(null); }} selected={s === value}>
            <Avatar src={LOGO[s]} sx={{ width: 20, height: 20, mr: 1 }} /> {s}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

